import { defineComponent, h, onBeforeUnmount, onMounted, onUpdated, ref, type ComponentPublicInstance } from "vue";
import { VueTelInput } from "vue-tel-input";

const isCountrySearchSpace = (event: KeyboardEvent): boolean =>
	event.key === " " && event.target instanceof HTMLInputElement && event.target.classList.contains("vti__search_box");

export const attachCountrySearchSpaceGuard = (root: HTMLElement | null): (() => void) => {
	const input = root?.querySelector<HTMLInputElement>(".vti__search_box");
	if (!input) return () => {};
	const stopParentKeyHandler = (event: KeyboardEvent) => {
		if (isCountrySearchSpace(event)) event.stopPropagation();
	};
	input.addEventListener("keydown", stopParentKeyHandler);
	return () => input.removeEventListener("keydown", stopParentKeyHandler);
};

export const hideDuplicateCountryOptions = (root: HTMLElement | null): void => {
	const seen = new Set<string>();
	root?.querySelectorAll<HTMLElement>(".vti__dropdown-item").forEach((item) => {
		const countryCode = item.querySelector<HTMLElement>(".vti__flag")?.className.split(" ").at(-1);
		if (!countryCode || !seen.has(countryCode)) {
			if (countryCode) seen.add(countryCode);
			item.hidden = false;
			return;
		}
		item.hidden = true;
		item.setAttribute("aria-hidden", "true");
	});
};

type TelCountry = { iso2: string; name: string; dialCode: string };

// vue-tel-input's names carry the native name too ("Sweden (Sverige)"); the hint wants the plain English one.
const englishCountryName = (country: TelCountry): string => {
	try {
		return new Intl.DisplayNames(["en"], { type: "region" }).of(country.iso2.toUpperCase()) ?? country.name;
	} catch {
		return country.name;
	}
};

const callListeners = (listeners: unknown, ...args: unknown[]): void => {
	for (const listener of [listeners].flat()) if (typeof listener === "function") listener(...args);
};

export const SafeVueTelInput = defineComponent({
	name: "SafeVueTelInput",
	inheritAttrs: false,
	props: {
		// LSP-22027: opt-in, because the hint needs a wrapping <div> — call sites whose scoped CSS or $el reads expect the
		// field itself as the root (e.g. FormEditorDialog's .phoneNumberField) keep the bare field.
		warnOnTypedCountryChange: { type: Boolean, default: false }
	},
	setup(props, { attrs, slots, expose }) {
		type VueTelInputInstance = ComponentPublicInstance<{
			$el: HTMLElement;
			blur: () => void;
			focus: () => void;
		}>;
		const inputRef = ref<VueTelInputInstance | null>(null);
		const focus = () => inputRef.value?.focus();
		const blur = () => inputRef.value?.blur();
		// vue-tel-input handles validation from the native input event; its
		// internal handler is not part of the child component's public API.
		const onInput = () =>
			inputRef.value?.$el.querySelector<HTMLInputElement>(".vti__phone")?.dispatchEvent(new Event("input", { bubbles: true }));
		expose({ focus, blur, onInput });

		// LSP-22027: vue-tel-input re-reads the country from what is typed and switches the flag on its own — with the
		// United States picked, "011…" is the US international prefix, so "0114644545" silently became Sweden. A country
		// the user did not pick (in the list, or by typing "+code") gets an amber hint under the field. Never blocks Send.
		let pickedCountryIso2: string | null = null; // the first country the field settles on, then each pick
		let activeCountry: TelCountry | null = null;
		let countryPickPending = false;
		const typedCountry = ref<TelCountry | null>(null);
		const markCountryPick = (event: Event) => {
			const target = event.target instanceof Element ? event.target : null;
			const isPick =
				event.type === "click"
					? !!target?.closest(".vti__dropdown-item")
					: (event as KeyboardEvent).key === "Enter" &&
						!!target?.closest(".vti__dropdown.open")?.querySelector(".vti__dropdown-item.highlighted");
			if (!isPick) return;
			countryPickPending = true;
			// Picking the country already shown changes nothing, so no country-changed follows — it still confirms that country.
			setTimeout(() => {
				if (!countryPickPending) return;
				countryPickPending = false;
				pickedCountryIso2 = activeCountry?.iso2 ?? pickedCountryIso2;
				typedCountry.value = null;
			});
		};
		const onCountryChanged = (country: TelCountry) => {
			activeCountry = country;
			if (
				countryPickPending ||
				pickedCountryIso2 === null ||
				String(attrs.modelValue ?? "")
					.trim()
					.startsWith("+")
			) {
				countryPickPending = false;
				pickedCountryIso2 = country.iso2;
				typedCountry.value = null;
			} else {
				typedCountry.value = country.iso2 === pickedCountryIso2 ? null : country;
			}
			callListeners(attrs.onCountryChanged, country);
		};
		let detachGuard = () => {};
		let observer: MutationObserver | undefined;
		const syncGuard = () => {
			detachGuard();
			const root = inputRef.value?.$el ?? null;
			detachGuard = attachCountrySearchSpaceGuard(root);
			hideDuplicateCountryOptions(root);
		};
		let pickListenerRoot: HTMLElement | null = null;
		onMounted(() => {
			syncGuard();
			const root = inputRef.value?.$el;
			if (root) {
				observer = new MutationObserver(syncGuard);
				observer.observe(root, { childList: true, subtree: true });
				// Capture phase: the pick is marked before vue-tel-input's own handler changes the country.
				pickListenerRoot = root;
				root.addEventListener("click", markCountryPick, true);
				root.addEventListener("keydown", markCountryPick, true);
			}
		});
		onUpdated(syncGuard);
		onBeforeUnmount(() => {
			detachGuard();
			observer?.disconnect();
			pickListenerRoot?.removeEventListener("click", markCountryPick, true);
			pickListenerRoot?.removeEventListener("keydown", markCountryPick, true);
		});
		return () => {
			const field = h(VueTelInput, { ...attrs, onCountryChanged, ref: inputRef }, slots);
			if (!props.warnOnTypedCountryChange) return field;
			const country = typedCountry.value;
			const hint =
				country && String(attrs.modelValue ?? "").trim()
					? h(
							"div",
							{
								class: "v-messages px-3 pt-1",
								// Amber, never red (LSP-21412): a heads-up, not an error. #B45309, the app's field attention colour.
								style: { color: "rgb(180, 83, 9)", opacity: 1 },
								role: "status",
								"data-test-id": "phone-country-changed-hint"
							},
							`This number is for ${englishCountryName(country)} (+${country.dialCode}). Check the country before sending.`
						)
					: null;
			return h("div", [field, hint]);
		};
	}
});
